import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import AdBanner from './AdBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  LayoutDashboard,
  Users,
  FileText,
  Package,
  Heart,
  Gift,
  Truck,
  BarChart3,
  Settings,
  Church,
  LogOut,
  Menu,
  Sun,
  Moon,
  ChevronRight,
  Upload,
  Megaphone,
  Mail,
  Brain
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/famiglie', icon: Users, label: 'Famiglie' },
  { path: '/magazzino', icon: Package, label: 'Magazzino' },
  { path: '/donatori', icon: Heart, label: 'Donatori' },
  { path: '/donazioni', icon: Gift, label: 'Donazioni' },
  { path: '/distribuzioni', icon: Truck, label: 'Distribuzioni' },
  { path: '/news', icon: Megaphone, label: 'Comunicazioni' },
  { path: '/report', icon: BarChart3, label: 'Report' },
  { path: '/import', icon: Upload, label: 'Import' },
];

const familyNavItems = [
  { path: '/portale', icon: LayoutDashboard, label: 'Il Mio Portale' },
  { path: '/news', icon: Megaphone, label: 'Comunicazioni' },
];

const adminItems = [
  { path: '/utenti', icon: Users, label: 'Utenti' },
  { path: '/impostazioni', icon: Settings, label: 'Impostazioni' },
];

const superAdminItems = [
  { path: '/chiese', icon: Church, label: 'Chiese / Associazioni' },
  { path: '/contatti', icon: Mail, label: 'Contatti Ricevuti' },
  { path: '/config-ai', icon: Brain, label: 'Configurazione AI' },
];

const NavItem = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group
        ${isActive 
          ? 'bg-primary text-primary-foreground shadow-sm' 
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`
      }
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium">{item.label}</span>
      <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
    </NavLink>
  );
};

const Sidebar = ({ mobile, onClose }) => {
  const { user, isAdmin, isSuperAdmin } = useAuth();

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-border/40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Heart className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-lg text-foreground">AGAPE HUB</h1>
            <p className="text-xs text-muted-foreground">Banco Alimentare</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {user?.ruolo === 'famiglia' ? (
          <>
            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Portale Famiglia
            </p>
            {familyNavItems.map(item => (
              <NavItem key={item.path} item={item} onClick={mobile ? onClose : undefined} />
            ))}
          </>
        ) : (
          <>
            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Menu Principale
            </p>
            {navItems.map(item => (
              <NavItem key={item.path} item={item} onClick={mobile ? onClose : undefined} />
            ))}

        {isAdmin() && (
          <>
            <p className="px-4 py-2 mt-6 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Amministrazione
            </p>
            {adminItems.map(item => (
              <NavItem key={item.path} item={item} onClick={mobile ? onClose : undefined} />
            ))}
          </>
        )}

        {isSuperAdmin() && (
          <>
            <p className="px-4 py-2 mt-6 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Super Admin
            </p>
            {superAdminItems.map(item => (
              <NavItem key={item.path} item={item} onClick={mobile ? onClose : undefined} />
            ))}
          </>
        )}
          </>
        )}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-border/40">
        <div className="flex items-center gap-3 px-2">
          <Avatar className="w-9 h-9">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {user?.nome?.[0]}{user?.cognome?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.nome} {user?.cognome}</p>
            <p className="text-xs text-muted-foreground truncate capitalize">
              {user?.ruolo?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    const item = [...navItems, ...adminItems, ...superAdminItems].find(i => path.startsWith(i.path));
    return item?.label || 'AGAPE HUB';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 z-40 w-64 h-screen bg-card border-r border-border/40 hidden lg:block">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[280px] sm:w-72">
          <Sidebar mobile onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/40">
          <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-8">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-10 w-10"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h2 className="font-heading font-semibold text-base sm:text-lg truncate">{getPageTitle()}</h2>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-10 w-10"
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 h-10 px-2 sm:px-3" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                        {user?.nome?.[0]}{user?.cognome?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline font-medium text-sm">{user?.nome}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.nome} {user?.cognome}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/impostazioni')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Impostazioni
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-4 lg:p-8 pb-16">
          {children}
        </main>
      </div>
      <AdBanner />
    </div>
  );
};

export default Layout;
